import { record } from '@rrweb/record';
import { session } from '@/globals/session';
import accountService from '@/services/accounts';

const SUBMIT_URL = 'https://rec.zeit.work/submit/';
const FLUSH_INTERVAL_MS = 10_000;

let stopFn: (() => void) | undefined;
let buffer: any[] = [];
let flushHandle: ReturnType<typeof setInterval> | undefined;
let sessionStartedAt: string | undefined;

function startRecording() {
  if (stopFn) return;

  buffer = [];
  sessionStartedAt = new Date().toISOString();

  stopFn = record({
    emit(event) {
      buffer.push(event);
    },
    plugins: [
      {
        name: 'rrweb/console',
        observer(cb: Function, win: Window) {
          function stringify(a: any): string {
            try { return typeof a === 'object' ? JSON.stringify(a) : String(a); }
            catch { try { return String(a); } catch { return '[unserializable]'; } }
          }

          // Intercept console methods
          const levels = ['log', 'info', 'warn', 'error', 'debug'] as const;
          const originals = new Map<string, Function>();
          for (const level of levels) {
            originals.set(level, win.console[level]);
            win.console[level] = function (...args: any[]) {
              cb({ level, payload: args.map(stringify) });
              originals.get(level)!.apply(this, args);
            };
          }

          // Capture uncaught errors
          const onError = (e: ErrorEvent) => {
            const msg = e.error?.stack || e.message || 'Unknown error';
            cb({ level: 'error', payload: [`[Uncaught] ${msg}`] });
          };
          win.addEventListener('error', onError);

          // Capture unhandled promise rejections
          const onRejection = (e: PromiseRejectionEvent) => {
            const reason = e.reason?.stack || e.reason?.message || stringify(e.reason);
            cb({ level: 'error', payload: [`[Unhandled Rejection] ${reason}`] });
          };
          win.addEventListener('unhandledrejection', onRejection);

          return () => {
            for (const level of levels) win.console[level] = originals.get(level) as any;
            win.removeEventListener('error', onError);
            win.removeEventListener('unhandledrejection', onRejection);
          };
        },
        options: {},
      },
      {
        name: 'rrweb/network',
        observer(cb: Function, win: Window) {
          function redactAuth(v: string): string {
            return v.replace(
              /([0-9a-f]{8})-([0-9a-f]{4})-([0-9a-f]{4})-([0-9a-f]{4})-([0-9a-f]{12})/gi,
              (_, g1) => `${g1}-****-****-****-************`,
            );
          }
          function headersToObj(h: any): Record<string, string> {
            const out: Record<string, string> = {};
            if (!h) return out;
            if (h instanceof Headers) { h.forEach((v: string, k: string) => { out[k] = v; }); }
            else if (Array.isArray(h)) { for (const [k, v] of h) out[k] = v; }
            else if (typeof h === 'object') { Object.assign(out, h); }
            for (const k of Object.keys(out)) {
              if (k.toLowerCase() === 'authorization') out[k] = redactAuth(out[k]);
            }
            return out;
          }
          const origFetch = win.fetch;
          win.fetch = function (input: any, init?: any) {
            const url = typeof input === 'string' ? input : input?.url ?? String(input);
            if (url.startsWith(SUBMIT_URL)) return origFetch.apply(this, [input, init] as any);
            const method = init?.method ?? 'GET';
            const headers = headersToObj(init?.headers ?? (typeof input === 'object' ? input?.headers : undefined));
            const startTime = Date.now();
            return origFetch.apply(this, [input, init] as any).then((res: Response) => {
              cb({ type: 'fetch', method, url, status: res.status, duration: Date.now() - startTime, headers });
              return res;
            }).catch((err: any) => {
              cb({ type: 'fetch', method, url, status: 0, error: String(err), duration: Date.now() - startTime, headers });
              throw err;
            });
          };
          const origOpen = win.XMLHttpRequest.prototype.open;
          const origSend = win.XMLHttpRequest.prototype.send;
          const origSetHeader = win.XMLHttpRequest.prototype.setRequestHeader;
          win.XMLHttpRequest.prototype.open = function (m: string, u: string) {
            (this as any).__rr = { method: m, url: u, headers: {} as Record<string, string> };
            return origOpen.apply(this, arguments as any);
          };
          win.XMLHttpRequest.prototype.setRequestHeader = function (k: string, v: string) {
            if ((this as any).__rr) (this as any).__rr.headers[k] = k.toLowerCase() === 'authorization' ? redactAuth(v) : v;
            return origSetHeader.apply(this, arguments as any);
          };
          win.XMLHttpRequest.prototype.send = function () {
            const rr = (this as any).__rr;
            if (rr) {
              const startTime = Date.now();
              this.addEventListener('loadend', () => {
                cb({ type: 'xhr', method: rr.method, url: rr.url, status: this.status, duration: Date.now() - startTime, headers: rr.headers });
              });
            }
            return origSend.apply(this, arguments as any);
          };
          return () => {
            win.fetch = origFetch;
            win.XMLHttpRequest.prototype.open = origOpen;
            win.XMLHttpRequest.prototype.send = origSend;
            win.XMLHttpRequest.prototype.setRequestHeader = origSetHeader;
          };
        },
        options: {},
      },
    ],
  });

  flushHandle = setInterval(submitEvents, FLUSH_INTERVAL_MS);
}

function stopRecording() {
  if (stopFn) {
    stopFn();
    stopFn = undefined;
  }
  if (flushHandle) {
    clearInterval(flushHandle);
    flushHandle = undefined;
  }
  submitEvents();
}

async function submitEvents() {
  if (buffer.length === 0) return;

  const events = buffer;
  buffer = [];

  try {
    await fetch(SUBMIT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        events,
        accountId: session.account?.id,
        institutionId: session.institution?.id,
        startedAt: sessionStartedAt,
        meta: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          screenWidth: screen.width,
          screenHeight: screen.height,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
        },
      }),
    });
  } catch {
    // Re-add events to buffer on failure so they aren't lost
    buffer = events.concat(buffer);
  }
}

async function handleSessionChange() {
  if (!localStorage.accessToken) return;

  const response = await accountService.list();
  if (!response) return;

  const user = response.data.results[0];
  if (user && user.is_web_recording) {
    startRecording();
  } else {
    stopRecording();
  }
}

export async function initRecorder() {
  await handleSessionChange();
  session.on('change', handleSessionChange);
  session.on('reset', stopRecording);
}
